import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { DELAYS } from '@/constants/timing';
import { TYPOGRAPHY, RESPONSIVE } from '@/design-system';
import { SearchInput } from "@/components/ui/search-input";
import { Search, ArrowRight, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { FAQ } from "@/hooks/useFAQs";
import { useSearchSuggestions } from "@/hooks/useSearchSuggestions";
import { useLocalizations } from "@/hooks/useLocalizations";

interface FAQSearchAutocompleteProps {
  faqs: FAQ[];
  value: string;
  onChange: (value: string) => void;
  onSelect: (faqId: string) => void;
  placeholder?: string;
}

export const FAQSearchAutocomplete = ({
  faqs,
  value,
  onChange,
  onSelect,
  placeholder,
}: FAQSearchAutocompleteProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const { data: trendingSuggestions = [] } = useSearchSuggestions(6);
  const { lComponents } = useLocalizations();

  // Show trending when input is empty or very short
  const showTrending = value.length < 2 && trendingSuggestions.length > 0;

  // Filter FAQs based on search query
  const suggestions = useMemo(() => {
    if (!value.trim() || value.length < 2) return [];
    const query = value.toLowerCase();
    return faqs
      .filter(faq => 
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.keywords?.some(k => k.toLowerCase().includes(query))
      )
      .slice(0, 5);
  }, [faqs, value]);

  // Open dropdown when there are suggestions or trending items
  useEffect(() => {
    setIsOpen(suggestions.length > 0 || showTrending);
    setHighlightedIndex(-1);
  }, [suggestions.length, showTrending]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const itemCount = showTrending ? trendingSuggestions.length : suggestions.length;
    if (!isOpen || itemCount === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < itemCount - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : itemCount - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (showTrending) {
            onChange(trendingSuggestions[highlightedIndex].suggestion_text);
          } else if (suggestions[highlightedIndex]) {
            onSelect(suggestions[highlightedIndex].id);
            setIsOpen(false);
            onChange('');
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  }, [isOpen, suggestions, highlightedIndex, onSelect, onChange, showTrending, trendingSuggestions]);

  const handleSelect = useCallback((faqId: string) => {
    onSelect(faqId);
    setIsOpen(false);
    onChange('');
    inputRef.current?.blur();
  }, [onSelect, onChange]);

  const handleTrendingClick = useCallback((text: string) => {
    onChange(text);
    inputRef.current?.focus();
  }, [onChange]);

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className="bg-primary/20 text-foreground rounded px-0.5">{part}</mark> : part
    );
  };

  const defaultPlaceholder = lComponents('faq_search.placeholder', { defaultValue: 'Search for answers' });

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <SearchInput
          ref={inputRef}
          placeholder={placeholder || defaultPlaceholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(suggestions.length > 0 || showTrending)}
          onBlur={() => setTimeout(() => setIsOpen(false), DELAYS.ANIMATION_NORMAL)}
          className="pl-12 h-14 text-lg"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-label={lComponents('faq_search.aria.label', { defaultValue: 'Search FAQs' })}
        />
      </div>
      {isOpen && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-80 overflow-auto"
          role="listbox"
        >
          {showTrending ? (
            <>
              <li className={cn(TYPOGRAPHY.labelXs, 'px-4 py-2 flex items-center gap-1.5 border-b')}>
                <TrendingUp className="h-3 w-3" />
                {lComponents('search_suggestions.trending.title', { defaultValue: 'Trending Searches' })}
              </li>
              {trendingSuggestions.map((suggestion, index) => (
                <li
                  key={suggestion.id}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onClick={() => handleTrendingClick(suggestion.suggestion_text)}
                  className={cn(
                    RESPONSIVE.flexRowCenterGap3,
                    'px-4 py-3 cursor-pointer border-b last:border-b-0',
                    "hover:bg-muted transition-colors",
                    index === highlightedIndex && "bg-muted"
                  )}
                >
                  <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="flex-1">{suggestion.suggestion_text}</span>
                  {suggestion.source_type === 'trending' && (
                    <TrendingUp className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  )}
                </li>
              ))}
              <li className={cn(TYPOGRAPHY.caption, 'px-4 py-2 border-t')}>
                {lComponents('faq_search.suggestions.hint', { defaultValue: 'Select a suggestion to view details.' })}
              </li>
            </>
          ) : (
            suggestions.map((faq, index) => (
              <li
                key={faq.id}
                role="option"
                aria-selected={index === highlightedIndex}
                onClick={() => handleSelect(faq.id)}
                className={cn(
                  RESPONSIVE.flexRowCenterGap3,
                  'px-4 py-3 cursor-pointer border-b last:border-b-0',
                  "hover:bg-muted transition-colors",
                  index === highlightedIndex && "bg-muted"
                )}
              >
                <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {highlightMatch(faq.question, value)}
                  </p>
                  <p className={cn(TYPOGRAPHY.cardDescription, 'truncate')}>
                    {faq.answer.substring(0, 80)}...
                  </p>
                </div>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
